import './App.css';

//-> Routing
import { Navigate, Route, Routes } from 'react-router-dom';
import { PrivateRoute, PublicRoute, RequireNavigationState } from './utils/route';

//-> API
import { Toaster } from 'react-hot-toast';
import { useSelector } from 'react-redux';
import { useGetUserProfileQuery } from './store/apiSlices/userApiSlice';
import PrimaryTheme from './theme/primaryTheme';

// -> Components
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import MainLayout from './components/layout/MainLayout';

// -> Pages
import { Button, CircularProgress } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import Stack from '@mui/material/Stack';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import ErrorBoundary from './components/common/ErrorBoundary';
import ExtraCostsContainer from './containers/ExtraCosts/ExtraCostsContainer';
import LubricantCategoriesContainer from './containers/Lubricants/LubricantCategoriesContainer';
import LubricantGroupsContainer from './containers/Lubricants/LubricantGroupsContainer';
import LubricantProductsContainer from './containers/Lubricants/LubricantProductsContainer';
import LubricantTypesContainer from './containers/Lubricants/LubricantTypesContainer';
import ManagementPeriodsContainer from './containers/ManagementPeriods/ManagementPeriodsContainer';
import RobsContainer from './containers/Robs/RobsContainer';
import AddRobsContainer from './containers/Robs/addRobs/AddRobsContainer';
import CompaniesCustomerContainer from './containers/companies/CompaniesCustomerContainer';
import CompaniesMakersContainer from './containers/companies/CompaniesMakersContainer';
import CompaniesSuppliersContainer from './containers/companies/CompaniesSuppliersContainer';
import DeliveryContainer from './containers/deliveries/DeliveryContainer';
import AddDeliveryContainer from './containers/deliveries/addDeliveries/AddDeliveryContainer';
import LoginContainer from './containers/login/LoginContainer';
import LubricationChartsContainer from './containers/lubricationCharts/LubricationChartsContainer';
import LubricationDetailContainer from './containers/lubricationCharts/LubricationDetailContainer';
import PriceListContainer from './containers/priceList/PriceListContainer';
import PriceListDetailContainer from './containers/priceList/PriceListDetailContainer';
import TypeInProductsContainer from './containers/typeInProducts/TypeInProductsContainer';
import { UnsavedChangesProvider } from './context/UnsavedChangesContext';
import { logger } from './services/logger/logger';
import { setAccessToken, setUserInfo } from './store/slices/userSlice';
import { useDocumentTitle } from './hooks/useDocumentTitle';
import {
  canAccessCompanies,
  canAccessExtraCosts,
  canAccessLubricationCharts,
  canAccessManagementPeriods,
  canAccessPricelists,
  canAccessReferenceData,
  canAccessRobAndDeliveries,
  canAccessTypeInProducts,
  canEditLubricationCharts,
  canEditRobAndDeliveries,
} from './utils/permissions';
import { clearAppStorage, clearPostLoginRedirect } from './utils/storage';

function PageNotFound() {
  const { t } = useTranslation();
  return (
    <Stack
      justifyContent="center"
      alignItems="center"
      sx={{ height: '90vh', textAlign: 'center', p: 3 }}
    >
      <h1 style={{ fontSize: '2rem', marginBottom: '1rem' }}>{t('common.notFound')}</h1>
    </Stack>
  );
}

function ThemedToaster() {
  const theme = useTheme();

  return (
    <Toaster
      position="top-right"
      toastOptions={{
        success: {
          style: { borderLeft: `4px solid ${theme.palette.success.main}` },
        },
        error: {
          style: { borderLeft: `4px solid ${theme.palette.error.main}` },
        },
      }}
    />
  );
}

function UnauthorizedPage() {
  const { t } = useTranslation();
  const theme = useTheme();
  return (
    <Stack
      justifyContent="center"
      alignItems="center"
      sx={{ height: '90vh', textAlign: 'center', p: 3 }}
    >
      <h1 style={{ fontSize: '2rem', marginBottom: '1rem' }}>{t('common.unauthorized')}</h1>
      <p style={{ fontSize: '1.2rem', color: theme.palette.text.secondary }}>
        {t('common.unauthorizedMessage')}
      </p>
    </Stack>
  );
}

function App() {
  useDocumentTitle();
  const dispatch = useDispatch();
  // Local storate or store
  const accessToken = useSelector((state: { user: any }) => state.user?.accessToken);

  const { t } = useTranslation();
  const { data, isLoading, isError, refetch } = useGetUserProfileQuery(undefined, {
    skip: !accessToken,
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });

  useEffect(() => {
    if (data) {
      dispatch(setUserInfo(data));
    }
  }, [data, dispatch]);

  // Proactive token refresh - refresh tokens before they expire
  useEffect(() => {
    if (!accessToken) return;

    const refreshToken = async () => {
      try {
        const { msalInstance } = await import('./config/msalConfig');
        const accounts = msalInstance.getAllAccounts();

        if (accounts && accounts.length > 0) {
          const account = accounts[0];
          const { azureApiScope } = await import('./config/msalConfig');
          if (!azureApiScope) return;
          const response = await msalInstance.acquireTokenSilent({
            scopes: [azureApiScope],
            account: account,
          });

          dispatch(setAccessToken(response.accessToken));
        }
      } catch (_error) {
        logger.error('Failed to refresh token', _error as Error);
      }
    };

    // Refresh token every 45 minutes (tokens typically expire after 1 hour)
    const intervalId = setInterval(refreshToken, 45 * 60 * 1000);

    // Initial refresh after 45 minutes
    const timeoutId = setTimeout(refreshToken, 45 * 60 * 1000);

    return () => {
      clearInterval(intervalId);
      clearTimeout(timeoutId);
    };
  }, [accessToken, dispatch]);

  const token = accessToken ?? '';
  const isAuthenticated = !!token;
  const authorityName = useSelector((state: any) => state.user.userInfo?.authorityName);

  // Login has completed and returned the user to their requested page - forget it
  useEffect(() => {
    if (isAuthenticated && authorityName) {
      clearPostLoginRedirect();
    }
  }, [isAuthenticated, authorityName]);

  // Profile request failed (e.g. 500 / network) - avoid an endless spinner.
  // 401 is handled by the axios interceptor (redirects to login).
  if (isAuthenticated && isError && !authorityName)
    return (
      <Stack
        justifyContent="center"
        alignItems="center"
        spacing={2}
        sx={{ height: '100vh', textAlign: 'center', p: 3 }}
      >
        <p>{t('common.profileLoadFailed')}</p>
        <Stack direction="row" spacing={2}>
          <Button variant="contained" onClick={() => refetch()}>
            {t('common.retry')}
          </Button>
          <Button
            variant="outlined"
            onClick={() => {
              clearAppStorage();
              window.location.href = '/login';
            }}
          >
            {t('buttons.signOut')}
          </Button>
        </Stack>
      </Stack>
    );

  if (isLoading || (isAuthenticated && !authorityName))
    return (
      <Stack justifyContent="center" alignItems="center" sx={{ height: '100vh' }}>
        <CircularProgress />
      </Stack>
    );

  // Handle: Redirect to
  const redirectToHandle = () => {
    if (!isAuthenticated) {
      return '/login';
    }
    return '/deliveries';
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <PrimaryTheme>
        <UnsavedChangesProvider>
          <ThemedToaster />
          <Routes>
            {/* 1 --> Public Routes */}
            <Route
              path="/"
              element={<PublicRoute isAuth={isAuthenticated} redirectTo={redirectToHandle()} />}
            >
              <Route index element={<Navigate to="/login" replace />} />
              <Route
                path="/login"
                element={
                  <ErrorBoundary>
                    <LoginContainer />
                  </ErrorBoundary>
                }
              />
            </Route>

            {/* 2 --> Private Routes */}
            <Route
              path="/"
              element={<PrivateRoute isAuth={isAuthenticated} redirectTo={redirectToHandle()} />}
            >
              <Route element={<MainLayout />}>
                <Route index element={<Navigate to="/deliveries" replace />} />

                {/* Role Protected Routes: Deliveries & ROB */}
                <Route
                  element={
                    <PrivateRoute
                      isAuth={isAuthenticated}
                      redirectTo={redirectToHandle()}
                      permission={canAccessRobAndDeliveries}
                    />
                  }
                >
                  <Route
                    path="/deliveries"
                    element={
                      <ErrorBoundary>
                        <DeliveryContainer />
                      </ErrorBoundary>
                    }
                  />
                  <Route
                    path="/rob"
                    element={
                      <ErrorBoundary>
                        <RobsContainer />
                      </ErrorBoundary>
                    }
                  />

                  {/* Add/Edit requires edit permission (view-only roles are blocked) */}
                  <Route
                    element={
                      <PrivateRoute
                        isAuth={isAuthenticated}
                        redirectTo={redirectToHandle()}
                        permission={canEditRobAndDeliveries}
                      />
                    }
                  >
                    <Route
                      path="/deliveries/add"
                      element={
                        <ErrorBoundary>
                          <AddDeliveryContainer />
                        </ErrorBoundary>
                      }
                    />
                    <Route
                      element={
                        <RequireNavigationState stateKey="deliveryData" redirectTo="/deliveries" />
                      }
                    >
                      <Route
                        path="/deliveries/edit/:id"
                        element={
                          <ErrorBoundary>
                            <AddDeliveryContainer />
                          </ErrorBoundary>
                        }
                      />
                    </Route>
                    <Route
                      path="/rob/add"
                      element={
                        <ErrorBoundary>
                          <AddRobsContainer />
                        </ErrorBoundary>
                      }
                    />
                    <Route
                      element={<RequireNavigationState stateKey="robsData" redirectTo="/rob" />}
                    >
                      <Route
                        path="/rob/edit/:id"
                        element={
                          <ErrorBoundary>
                            <AddRobsContainer />
                          </ErrorBoundary>
                        }
                      />
                    </Route>
                  </Route>
                </Route>

                {/* Role Protected Routes: Reference Data */}
                <Route
                  element={
                    <PrivateRoute
                      isAuth={isAuthenticated}
                      redirectTo={redirectToHandle()}
                      permission={canAccessReferenceData}
                    />
                  }
                >
                  {/* Lubricants */}
                  <Route
                    path="/lubricants/products"
                    element={
                      <ErrorBoundary>
                        <LubricantProductsContainer />
                      </ErrorBoundary>
                    }
                  />
                  <Route
                    path="/lubricants/categories"
                    element={
                      <ErrorBoundary>
                        <LubricantCategoriesContainer />
                      </ErrorBoundary>
                    }
                  />
                  <Route
                    path="/lubricants/types"
                    element={
                      <ErrorBoundary>
                        <LubricantTypesContainer />
                      </ErrorBoundary>
                    }
                  />
                  <Route
                    path="/lubricants/groups"
                    element={
                      <ErrorBoundary>
                        <LubricantGroupsContainer />
                      </ErrorBoundary>
                    }
                  />
                </Route>

                {/* Role Protected Routes: Extra Costs */}
                <Route
                  element={
                    <PrivateRoute
                      isAuth={isAuthenticated}
                      redirectTo={redirectToHandle()}
                      permission={canAccessExtraCosts}
                    />
                  }
                >
                  <Route
                    path="/extra-costs"
                    element={
                      <ErrorBoundary>
                        <ExtraCostsContainer />
                      </ErrorBoundary>
                    }
                  />
                </Route>

                {/* Role Protected Routes: Companies */}
                <Route
                  element={
                    <PrivateRoute
                      isAuth={isAuthenticated}
                      redirectTo={redirectToHandle()}
                      permission={canAccessCompanies}
                    />
                  }
                >
                  <Route
                    path="/companies/makers"
                    element={
                      <ErrorBoundary>
                        <CompaniesMakersContainer />
                      </ErrorBoundary>
                    }
                  />
                  <Route
                    path="/companies/suppliers"
                    element={
                      <ErrorBoundary>
                        <CompaniesSuppliersContainer />
                      </ErrorBoundary>
                    }
                  />
                  <Route
                    path="/companies/customer"
                    element={
                      <ErrorBoundary>
                        <CompaniesCustomerContainer />
                      </ErrorBoundary>
                    }
                  />
                </Route>

                {/* Role Protected Routes: Lubrication Charts */}
                <Route
                  element={
                    <PrivateRoute
                      isAuth={isAuthenticated}
                      redirectTo={redirectToHandle()}
                      permission={canAccessLubricationCharts}
                    />
                  }
                >
                  <Route
                    path="/lubrication-charts"
                    element={
                      <ErrorBoundary>
                        <LubricationChartsContainer />
                      </ErrorBoundary>
                    }
                  />
                  <Route
                    element={
                      <RequireNavigationState stateKey="data" redirectTo="/lubrication-charts" />
                    }
                  >
                    <Route
                      path="/lubrication-view"
                      element={
                        <ErrorBoundary>
                          <LubricationDetailContainer />
                        </ErrorBoundary>
                      }
                    />
                    <Route
                      element={
                        <PrivateRoute
                          isAuth={isAuthenticated}
                          redirectTo={redirectToHandle()}
                          permission={canEditLubricationCharts}
                        />
                      }
                    >
                      <Route
                        path="/lubrication-edit"
                        element={
                          <ErrorBoundary>
                            <LubricationDetailContainer />
                          </ErrorBoundary>
                        }
                      />
                    </Route>
                  </Route>
                </Route>

                {/* Role Protected Routes: Management Periods */}
                <Route
                  element={
                    <PrivateRoute
                      isAuth={isAuthenticated}
                      redirectTo={redirectToHandle()}
                      permission={canAccessManagementPeriods}
                    />
                  }
                >
                  <Route
                    path="/management-periods"
                    element={
                      <ErrorBoundary>
                        <ManagementPeriodsContainer />
                      </ErrorBoundary>
                    }
                  />
                </Route>

                {/* Role Protected Routes: Price List */}
                <Route
                  element={
                    <PrivateRoute
                      isAuth={isAuthenticated}
                      redirectTo={redirectToHandle()}
                      permission={canAccessPricelists}
                    />
                  }
                >
                  <Route
                    path="/price-list"
                    element={
                      <ErrorBoundary>
                        <PriceListContainer />
                      </ErrorBoundary>
                    }
                  />
                  <Route
                    element={<RequireNavigationState stateKey="data" redirectTo="/price-list" />}
                  >
                    <Route
                      path="/price-list-details"
                      element={
                        <ErrorBoundary>
                          <PriceListDetailContainer />
                        </ErrorBoundary>
                      }
                    />
                  </Route>
                </Route>

                {/* Role Protected Routes: Type In Products */}
                <Route
                  element={
                    <PrivateRoute
                      isAuth={isAuthenticated}
                      redirectTo={redirectToHandle()}
                      permission={canAccessTypeInProducts}
                    />
                  }
                >
                  <Route
                    path="/type-in-lubricants"
                    element={
                      <ErrorBoundary>
                        <TypeInProductsContainer />
                      </ErrorBoundary>
                    }
                  />
                </Route>

                {/* 3 --> Fallback Routes (inside the layout so navigation stays available) */}
                <Route path="/unauthorized" element={<UnauthorizedPage />} />
                <Route path="*" element={<PageNotFound />} />
              </Route>
            </Route>
          </Routes>
        </UnsavedChangesProvider>
      </PrimaryTheme>
    </LocalizationProvider>
  );
}

export default App;
